import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, Edit, Trash2, Upload, Download, Save, X, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import SearchableSelect from "@/components/SearchableSelect";
import BulkDeleteBar from "@/components/BulkDeleteBar";

const UNIT_OPTIONS = [
  { value: "Kg", label: "Kg" },
  { value: "Liters", label: "Liters" },
  { value: "Nos", label: "Nos" },
];

const PURCHASE_GROUP_OPTIONS = [
  { value: "direct_purchase", label: "Direct Purchase" },
  { value: "indirect_purchase", label: "Indirect Purchase" },
  { value: "labour_work", label: "Labour Work" },
];

const PG_LABEL = Object.fromEntries(PURCHASE_GROUP_OPTIONS.map((o) => [o.value, o.label]));

export default function ItemMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [makes, setMakes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rmTypes, setRmTypes] = useState([]);
  const [rmTypesRaw, setRmTypesRaw] = useState([]); // {id,name}
  const [grades, setGrades] = useState([]);
  const [gradesRaw, setGradesRaw] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    // The primary `/items` call is mandatory; the others are best-effort so
    // a single failing endpoint never wipes the items grid.
    const [l, m, c, rt, gr] = await Promise.all([
      api.get("/items"),
      api.get("/items/makes").catch(() => ({ data: [] })),
      api.get("/categories").catch(() => ({ data: [] })),
      api.get("/rm-types").catch(() => ({ data: [] })),
      api.get("/grades").catch(() => ({ data: [] })),
    ]);
    setList(l.data);
    setMakes(m.data || []);
    setCategories((c.data || []).map((x) => x.name));
    setRmTypesRaw(rt.data || []);
    setRmTypes((rt.data || []).map((x) => x.name));
    setGradesRaw(gr.data || []);
    setGrades((gr.data || []).map((x) => x.name));
    setSelected([]);
  };
  useEffect(() => { load(); }, []);

  const blank = { description: "", category: "", make: "", purchase_group: "", item_specification: "", unit: "Nos", hsn_code: "", item_code: "", grade: "", rm_type: "" };

  const addRmType = async () => {
    const name = window.prompt("New Raw Material Type name (e.g. Ferrous, Non-Ferrous, Brass):");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/rm-types", { name: name.trim() });
      setRmTypes((prev) => [...prev, data.name].sort((a, b) => a.localeCompare(b)));
      setRmTypesRaw((prev) => [...prev, data]);
      setEdit((e) => (e ? { ...e, rm_type: data.name } : e));
      toast.success(`Added "${data.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add RM Type");
    }
  };

  const deleteRmType = async () => {
    const target = (edit?.rm_type || "").trim();
    if (!target) return toast.error("Pick a type from the dropdown to delete it");
    const row = rmTypesRaw.find((r) => r.name === target);
    if (!row) return toast.error("Type not found in master");
    if (!window.confirm(`Delete RM Type "${target}" from the master? Items already saved with this type will keep their value.`)) return;
    try {
      await api.delete(`/rm-types/${row.id}`);
      setRmTypes((prev) => prev.filter((n) => n !== target));
      setRmTypesRaw((prev) => prev.filter((r) => r.id !== row.id));
      setEdit((e) => (e ? { ...e, rm_type: "" } : e));
      toast.success(`Deleted "${target}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const addGrade = async () => {
    const name = window.prompt("New Grade name (e.g. EN8, EN19, SS304, Aluminium 6061):");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/grades", { name: name.trim() });
      setGrades((prev) => [...prev, data.name].sort((a, b) => a.localeCompare(b)));
      setGradesRaw((prev) => [...prev, data]);
      setEdit((e) => (e ? { ...e, grade: data.name } : e));
      toast.success(`Added "${data.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add Grade");
    }
  };

  const deleteGrade = async () => {
    const target = (edit?.grade || "").trim();
    if (!target) return toast.error("Pick a grade from the dropdown to delete it");
    const row = gradesRaw.find((r) => r.name === target);
    if (!row) return toast.error("Grade not found in master");
    if (!window.confirm(`Delete Grade "${target}" from the master? Items already saved with this grade will keep their value.`)) return;
    try {
      await api.delete(`/grades/${row.id}`);
      setGrades((prev) => prev.filter((n) => n !== target));
      setGradesRaw((prev) => prev.filter((r) => r.id !== row.id));
      setEdit((e) => (e ? { ...e, grade: "" } : e));
      toast.success(`Deleted "${target}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async () => {
    if (!edit.description?.trim() || !edit.category?.trim()) return toast.error("Description and Category required");
    try {
      if (edit.id) await api.put(`/items/${edit.id}`, edit);
      else await api.post("/items", edit);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}/items/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "item_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/items/import", fd);
    toast.success(`Imported ${data.inserted} items`);
    load();
  };

  const filtered = list.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (r.item_code || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s)
    );
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) =>
    setSelected((s) => (ids.every((id) => s.includes(id)) ? s.filter((x) => !ids.includes(x)) : Array.from(new Set([...s, ...ids]))));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Masters</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Item Master</h1>
          <p className="text-sm text-slate-500 mt-1">Item codes auto-generate as <span className="font-mono">TP-[Category Initial]-[Auto Number]</span>. Same item with different MAKE gets a unique code.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="item-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code or description…"
              className="pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
          <button onClick={downloadTemplate} data-testid="item-template-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Download size={14} /> Template</button>
          {canManage && (
            <>
              <input type="file" accept=".xlsx" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
              <button onClick={() => fileRef.current?.click()} data-testid="item-import-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Upload size={14} /> Import Excel</button>
              <button onClick={() => setEdit({ ...blank })} data-testid="item-add-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold"><Plus size={14} /> Add Item</button>
            </>
          )}
        </div>
      </div>

      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-2 gap-3">
          <div><Lbl>Item Description</Lbl><input data-testid="item-desc-input" autoFocus value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inputCls} /></div>
          <div>
            <Lbl>Category</Lbl>
            <SearchableSelect
              testid="item-category-input"
              options={categories.map((n) => ({ value: n, label: n }))}
              value={edit.category || ""}
              onChange={(v) => setEdit({ ...edit, category: v })}
              placeholder="Select category (use Category Master to add)"
            />
          </div>
          <div>
            <Lbl>Make (Manufacturer)</Lbl>
            <SearchableSelect
              testid="item-make-input"
              options={makes.map((m) => ({ value: m, label: m }))}
              value={edit.make || ""}
              onChange={(v) => setEdit({ ...edit, make: v })}
              placeholder="Search or add new make"
              allowCustom
            />
          </div>
          <div>
            <Lbl>Purchase Group</Lbl>
            <SearchableSelect
              testid="item-pg-input"
              options={PURCHASE_GROUP_OPTIONS}
              value={edit.purchase_group || ""}
              onChange={(v) => setEdit({ ...edit, purchase_group: v })}
              placeholder="Direct / Indirect / Labour"
            />
          </div>
          <div>
            <Lbl>Item Code{(edit.category || "").trim().toLowerCase() === "raw material" ? " (manual entry)" : " (auto-generated)"}</Lbl>
            <input
              value={edit.item_code}
              onChange={(e) => setEdit({ ...edit, item_code: e.target.value })}
              readOnly={(edit.category || "").trim().toLowerCase() !== "raw material"}
              data-testid="item-code-input"
              className={`${inputCls} font-mono ${(edit.category || "").trim().toLowerCase() !== "raw material" ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
              placeholder={(edit.category || "").trim().toLowerCase() === "raw material" ? "Type your code (e.g. RM-AL-001)" : "auto: TP-{Cat}-{###}"}
            />
          </div>
          {(edit.category || "").trim().toLowerCase() === "raw material" && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Lbl>Grade</Lbl>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addGrade}
                      data-testid="add-grade-btn"
                      className="text-[10px] uppercase tracking-wider font-semibold text-[#1D2951] hover:underline inline-flex items-center gap-1"
                    >
                      <Plus size={10} /> Add Grade
                    </button>
                    {edit.grade && (
                      <button
                        type="button"
                        onClick={deleteGrade}
                        data-testid="delete-grade-btn"
                        className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 hover:underline inline-flex items-center gap-1"
                      >
                        <Trash2 size={10} /> Delete
                      </button>
                    )}
                  </div>
                </div>
                <SearchableSelect
                  testid="item-grade-input"
                  options={grades.map((g) => ({ value: g, label: g }))}
                  value={edit.grade || ""}
                  onChange={(v) => setEdit({ ...edit, grade: v })}
                  placeholder="Search or pick a grade…"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Lbl>Raw Material Type</Lbl>
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addRmType}
                      data-testid="add-rm-type-btn"
                      className="text-[10px] uppercase tracking-wider font-semibold text-[#1D2951] hover:underline inline-flex items-center gap-1"
                    >
                      <Plus size={10} /> Add Type
                    </button>
                    {edit.rm_type && (
                      <button
                        type="button"
                        onClick={deleteRmType}
                        data-testid="delete-rm-type-btn"
                        className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 hover:underline inline-flex items-center gap-1"
                      >
                        <Trash2 size={10} /> Delete
                      </button>
                    )}
                  </div>
                </div>
                <SearchableSelect
                  testid="item-rm-type-input"
                  options={rmTypes.map((t) => ({ value: t, label: t }))}
                  value={edit.rm_type || ""}
                  onChange={(v) => setEdit({ ...edit, rm_type: v })}
                  placeholder="Search or pick a type…"
                />
              </div>
            </>
          )}
          <div>
            <Lbl>Unit</Lbl>
            <SearchableSelect
              testid="item-unit-input"
              options={UNIT_OPTIONS}
              value={edit.unit || "Nos"}
              onChange={(v) => setEdit({ ...edit, unit: v })}
              placeholder="Kg / Liters / Nos"
            />
          </div>
          <div><Lbl>HSN Code</Lbl><input value={edit.hsn_code} onChange={(e) => setEdit({ ...edit, hsn_code: e.target.value })} className={`${inputCls} font-mono`} /></div>
          <div className="col-span-2"><Lbl>Item Specification</Lbl><textarea value={edit.item_specification} onChange={(e) => setEdit({ ...edit, item_specification: e.target.value })} className={`${inputCls} min-h-[60px]`} /></div>
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><X size={14} /> Cancel</button>
            <button onClick={save} data-testid="item-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
          </div>
        </div>
      )}

      <BulkDeleteBar resource="items" selectedIds={selected} onClear={() => setSelected([])} onDeleted={load} testid="item-bulk" />

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            {canManage && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="item-select-all"
                  checked={filtered.length > 0 && filtered.every((r) => selected.includes(r.id))}
                  onChange={() => toggleAll(filtered.map((r) => r.id))}
                />
              </th>
            )}
            <th className="text-left px-6 py-3">Code</th>
            <th className="text-left px-6 py-3">Description</th>
            <th className="text-left px-6 py-3">Category</th>
            <th className="text-left px-6 py-3">Make</th>
            <th className="text-left px-6 py-3">Purchase Group</th>
            <th className="text-left px-6 py-3">Unit</th>
            <th className="text-left px-6 py-3">HSN</th>
            {canManage && <th className="text-right px-6 py-3">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={canManage ? 9 : 7} className="text-center py-12 text-slate-400">No items</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                {canManage && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(c.id)}
                      onChange={() => toggle(c.id)}
                      data-testid={`item-select-${c.id}`}
                    />
                  </td>
                )}
                <td className="px-6 py-3 font-mono font-semibold">{c.item_code}</td>
                <td className="px-6 py-3">{c.description}</td>
                <td className="px-6 py-3">{c.category}</td>
                <td className="px-6 py-3">{c.make || "—"}</td>
                <td className="px-6 py-3">
                  {c.purchase_group ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#1D2951]/10 text-[#1D2951]">
                      {PG_LABEL[c.purchase_group] || c.purchase_group}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-6 py-3">{c.unit}</td>
                <td className="px-6 py-3 font-mono text-xs">{c.hsn_code || "—"}</td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ ...c })} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(c.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const Lbl = ({ children }) => (<div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>);
